import React, { useEffect, useState } from "react";
import { apiClient } from "../../../../api/client";
import { url } from "../../../../api";
import { EveningIcon, MiddayIcon, MorningIcon, NightIcon } from "../../components/shared/TimeOfDayIcons";

interface CalendarEntry {
  date: string;
  scheduledTime: string;
  timezone: string;
  utcOffset: number;
  status: string;
  localTimeDisplay: string;
  // optional legacy field; UI uses utcOffset as absolute shift
  delta?: number | null;
}

interface AOTDCalendarBoardProps {
  className?: string;
}

/**
 * determines the time-of-day icon based on the scheduled time in user's local timezone
 * night: 00:00–05:59 and 18:00–23:59
 * morning: 06:00–11:59
 * midday: 12:00–17:59
 */
const getTimeIcon = (scheduledTimeISO: string) => {
  const date = new Date(scheduledTimeISO);
  const localHour = date.getHours();

  if (localHour >= 6 && localHour < 12) {
    return <MorningIcon />; // morning
  }

  if (localHour >= 12 && localHour < 16) {
    return <MiddayIcon />; // midday
  }

  if (localHour >= 16 && localHour < 20) {
    return <EveningIcon />; // evening
  }

  return <NightIcon />; // night
};

/**
 * formats the scheduled time in user's local timezone for tooltip
 */
const formatLocalTime = (scheduledTimeISO: string): string => {
  const date = new Date(scheduledTimeISO);
  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  });
};

/**
 * formats the shift as a string (0, +2, +12)
 */
const formatShift = (shift: number): string => {
  if (shift === 0) return "0";
  return `+${shift}`;
};

/**
 * formats the day label (Today, Mon, Tue, etc.)
 */
const getDayLabel = (dateString: string, index: number): string => {
  if (index === 0) return "Today";

  const date = new Date(dateString + "T00:00:00");
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return days[date.getDay()];
};

const AOTDCalendarBoard: React.FC<AOTDCalendarBoardProps> = ({ className = "" }) => {
  const [entries, setEntries] = useState<CalendarEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCalendar = async () => {
      try {
        const response = await apiClient.get(`${url}/api/aotd-calendar/week`);
        if (response.data.success && response.data.data?.entries) {
          // take only the first 5 entries
          setEntries(response.data.data.entries.slice(0, 5));
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          // calendar feature not enabled - silently hide
          setError("disabled");
        } else {
          console.error("Error fetching calendar:", err);
          setError("Failed to load calendar");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCalendar();
  }, []);

  // don't render anything if calendar is disabled or there's an error
  if (error === "disabled" || loading || entries.length === 0) {
    return null;
  }

  if (error) {
    return null;
  }

  return (
    <div className={`flex flex-col items-center ${className}`}>
      <div className="flex gap-1 sm:gap-2">
        {entries.map((entry, index) => {
          const timeIcon = getTimeIcon(entry.scheduledTime);
          const localTimeStr = formatLocalTime(entry.scheduledTime);

          return (
            <div
              key={entry.date}
              className="flex flex-col items-center justify-center bg-gray-100 border border-gray-300 rounded-md p-1 sm:p-2 min-w-[48px] sm:min-w-[56px] cursor-default"
              title={`AOTD at ${localTimeStr}`}
            >
              {/* row 1: day label */}
              <span className="text-xs font-semibold text-gray-700">
                {getDayLabel(entry.date, index)}
              </span>

              {/* row 2: time icon */}
              <span className="text-lg sm:text-xl my-0.5">
                {timeIcon}
              </span>

              {/* row 3: absolute shift from 04:50 UTC */}
              <span
                className={`text-xs font-mono ${
                  entry.utcOffset === 0 ? "text-gray-500" : "text-green-600"
                }`}
              >
                {formatShift(entry.utcOffset)}
              </span>
            </div>
          );
        })}
      </div>
      <span className="text-xs text-gray-400 mt-1">
        AOTD time shift from 04:50 UTC
      </span>
    </div>
  );
};

export default AOTDCalendarBoard;
